import Logger from '~/utils/logger/logger';

const FLEET_CACHE_PREFIX = 'fleet-web-';
// Must match API_CACHE_NAME in src/app/service-worker.ts (emitted standalone, so it can't be imported).
export const FLEET_API_CACHE_NAME = `${FLEET_CACHE_PREFIX}api-v1`;

type ServiceWorkerAuthErrorMessage = {
  type: 'SW_AUTH_ERROR';
  status?: number;
  url?: string;
};

export const addServiceWorkerAuthErrorListener = (
  onAuthError: (message: ServiceWorkerAuthErrorMessage) => void
): (() => void) => {
  if (!('serviceWorker' in navigator)) {
    return () => undefined;
  }

  const handler = (event: MessageEvent) => {
    const data = event.data as ServiceWorkerAuthErrorMessage | undefined;
    if (data?.type !== 'SW_AUTH_ERROR') {
      return;
    }
    onAuthError(data);
  };

  navigator.serviceWorker.addEventListener('message', handler);
  return () => navigator.serviceWorker.removeEventListener('message', handler);
};

export const shouldRegisterServiceWorker = (): boolean => {
  // Enabled in production, opt-in in development.
  return process.env.NODE_ENV === 'production' || process.env.ENABLE_SERVICE_WORKER === 'true';
};

export const registerServiceWorker = async (): Promise<void> => {
  if (!shouldRegisterServiceWorker()) {
    return;
  }

  if (!('serviceWorker' in navigator)) {
    return;
  }

  try {
    const publicUrl = String(process.env.PUBLIC_URL ?? '').replace(/\/$/, '');
    const scriptUrl = `${publicUrl}/service-worker.js`;
    const scope = publicUrl ? `${publicUrl}/` : '/';
    await navigator.serviceWorker.register(scriptUrl, { type: 'module', scope });
  } catch (err) {
    Logger.error('ServiceWorker registration failed:', err);
  }
};

export const clearServiceWorkerCaches = async (): Promise<number> => {
  if (!('serviceWorker' in navigator)) {
    return 0;
  }

  if (!('caches' in globalThis)) {
    return 0;
  }

  const cacheKeys = await globalThis.caches.keys();
  for (const cacheKey of cacheKeys) {
    await globalThis.caches.delete(cacheKey);
  }

  Logger.log(`Deleted ${cacheKeys.length} service worker caches`, cacheKeys);
  return cacheKeys.length;
};

/**
 * Clears fleet service worker caches.
 */
export const clearFleetServiceWorkerCaches = async (): Promise<number> => {
  if (!('caches' in globalThis)) {
    return 0;
  }

  const cacheKeys = await globalThis.caches.keys();
  const fleetCacheKeys = cacheKeys.filter((key) => key.startsWith(FLEET_CACHE_PREFIX));
  await Promise.all(fleetCacheKeys.map((key) => globalThis.caches.delete(key)));

  Logger.log(`Deleted ${fleetCacheKeys.length} Fleet service worker caches`, fleetCacheKeys);
  return fleetCacheKeys.length;
};

/**
 * Drops the service worker's cached `/users/current` response, so the next launch reads the user
 * from the network instead of a snapshot taken before the user changed their preferences.
 */
export const deleteCachedCurrentUser = async (): Promise<boolean> => {
  if (!('caches' in globalThis)) {
    return false;
  }

  try {
    const cache = await globalThis.caches.open(FLEET_API_CACHE_NAME);
    return await cache.delete(`${process.env.ENDPOINT_BFF}/users/current`, { ignoreVary: true });
  } catch (err) {
    Logger.error('Could not delete cached current user', err);
    return false;
  }
};
